import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useCreateRequest, useRequestedCreate } from '../lib/createRequest'
import {
  useApprenticeships,
  useDeleteIntranetLink,
  useIntranetUnseen,
  useMarkIntranetSeen,
  useIntranetEvent,
  useIntranetEvents,
  useIntranetLinks,
  useIntranetPost,
  useIntranetPosts,
  useSaveIntranetLink
} from '../api/queries'
import type { IntranetEvent, IntranetLink, IntranetPost, IntranetUnseen } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { EventCard, EventForm, LinkTile, PhoneList, PostCard, PostForm } from '../components/IntranetBits'
import { DocumentsTab } from '../components/IntranetDocuments'
import { GroupsTab, TasksTab } from '../components/IntranetGroupBits'
import { WorkEnvironmentTab } from '../components/IntranetWorkEnvironment'
import { IdeasTab } from '../components/IntranetIdeas'
import { PollsTab } from '../components/IntranetPolls'
import { OnCallTab } from '../components/IntranetOnCall'
import { KudosTab, MarketTab, TipsTab } from '../components/IntranetCommunity'
import { MealsTab } from '../components/IntranetMeals'
import { MeetingsTab } from '../components/IntranetMeetings'
import { MusTab } from '../components/IntranetMus'
import { ApprenticesTab } from '../components/IntranetApprentices'
import { CelebrationsBox } from '../components/Celebrations'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'
import { useModuleEnabled } from '../navigation'

type Tab = 'news' | 'meetings' | 'mus' | 'apprentices' | 'oncall' | 'meals' | 'kudos' | 'tips' | 'market' | 'events' | 'polls' | 'tasks' | 'incidents' | 'ideas' | 'documents' | 'groups' | 'phones' | 'links'

/**
 * Fanerne er samlet i fire grupper, så man ser 4-6 runde knapper ad gangen i stedet
 * for 19 på stribe (Simon 2026-10-28). Rækkefølgen her er rækkefølgen på skærmen.
 */
type TabGroup = 'daily' | 'community' | 'staff' | 'info'

const GROUPS: { key: TabGroup; label: string; icon: string }[] = [
  { key: 'daily', label: 'Hverdag', icon: '☀️' },
  { key: 'community', label: 'Fællesskab', icon: '🤝' },
  { key: 'staff', label: 'Personale', icon: '👔' },
  { key: 'info', label: 'Info', icon: '📚' }
]

// `module` er funktionen, fanen hører til (beslutning 119) - Superadmin kan slå den fra under Moduler. Nyheder,
// arbejdsgrupper og telefonlisten er selve intranettet.
const TABS: { key: Tab; label: string; icon: string; group: TabGroup; module?: string; anyOf?: string[] }[] = [
  // --- Hverdag ---
  { key: 'news', label: 'Nyheder', icon: '📣', group: 'daily' },
  { key: 'events', label: 'Kalender', icon: '📅', group: 'daily', module: 'intranet_events' },
  // Vagtplan og tilkaldevagt (beslutning 120).
  { key: 'oncall', label: 'Vagt', icon: '📟', group: 'daily', module: 'intranet_oncall' },
  // Faelles maaltider (beslutning 124).
  { key: 'meals', label: 'Frokost', icon: '🍽', group: 'daily', module: 'intranet_meals' },
  { key: 'tasks', label: 'Opgaver', icon: '✅', group: 'daily', module: 'intranet_tasks' },
  // --- Faellesskab ---
  // Ros, ugens faglige tip og koeb/salg (beslutning 121).
  { key: 'kudos', label: 'Ros', icon: '🙌', group: 'community', module: 'intranet_kudos' },
  { key: 'tips', label: 'Faglige tip', icon: '💡', group: 'community', module: 'intranet_tips' },
  // Hurtige afstemninger (beslutning 116).
  { key: 'polls', label: 'Afstemninger', icon: '📊', group: 'community', module: 'intranet_polls' },
  // Ideboksen (beslutning 115).
  { key: 'ideas', label: 'Idéboks', icon: '💭', group: 'community', module: 'intranet_ideas' },
  { key: 'market', label: 'Køb og salg', icon: '🛒', group: 'community', module: 'intranet_market' },
  // --- Personale ---
  // Personalemoeder (beslutning 128).
  { key: 'meetings', label: 'Møder', icon: '🗓', group: 'staff', module: 'intranet_meetings' },
  // MUS-samtaler (beslutning 129) - indholdet ser kun de to.
  { key: 'mus', label: 'MUS', icon: '💬', group: 'staff', module: 'intranet_mus' },
  // Laerlingeplan (beslutning 130) - kun for kontoret og dem, der er med i en plan.
  { key: 'apprentices', label: 'Lærling', icon: '🎓', group: 'staff', module: 'intranet_apprentices' },
  // Naerved-haendelser, APV og kemikalier (beslutning 113 og 125) - fanen er der, naar en af dem er slaaet til.
  { key: 'incidents', label: 'Arbejdsmiljø', icon: '🦺', group: 'staff', anyOf: ['intranet_incidents', 'intranet_apv', 'intranet_chemicals'] },
  // --- Info ---
  { key: 'documents', label: 'Håndbog', icon: '📄', group: 'info', module: 'intranet_documents' },
  { key: 'phones', label: 'Telefonliste', icon: '📞', group: 'info' },
  { key: 'groups', label: 'Arbejdsgrupper', icon: '👥', group: 'info' },
  { key: 'links', label: 'Genveje', icon: '🔗', group: 'info', module: 'intranet_links' }
]

/**
 * Husets egen side (docs/DECISIONS.md beslutning 100).
 *
 * <b>Én side med faner</b> frem for fire menupunkter: det er alt sammen "det
 * interne", og en montør skal kunne finde det ét sted.
 */
export function IntranetPage() {
  const { isIntranetAdmin: isAdmin } = useAuth()
  const moduleEnabled = useModuleEnabled()
  // Laerlingefanen (beslutning 130) kun for kontoret og dem, der er med i en plan - resten har intet at se dér.
  const apprenticesOn = moduleEnabled('intranet_apprentices')
  const { data: apprenticeships } = useApprenticeships(apprenticesOn && !isAdmin)
  const tabs = TABS.filter((t) => (t.anyOf ? t.anyOf.some((m) => moduleEnabled(m)) : moduleEnabled(t.module)))
    .filter((t) => t.key !== 'apprentices' || isAdmin || (apprenticeships?.length ?? 0) > 0)
  // ?fane= - beskeden om en naerved-haendelse aabner paa den rigtige fane.
  const [params] = useSearchParams()
  const [tab, setTab] = useState<Tab>(() => {
    const wanted = params.get('fane')
    return TABS.some((t) => t.key === wanted) ? (wanted as Tab) : 'news'
  })
  const markSeen = useMarkIntranetSeen()

  // Fra "+"-menuen: skift til den rigtige fane. Fanen selv åbner formularen og rydder parameteren.
  const requested = useRequestedCreate()
  useEffect(() => {
    if (requested === 'opslag') setTab('news')
    if (requested === 'begivenhed') setTab('events')
    if (requested === 'opgave') setTab('tasks')
    if (requested === 'naerved') setTab('incidents')
    if (requested === 'ide') setTab('ideas')
    if (requested === 'afstemning') setTab('polls')
    if (requested === 'ros') setTab('kudos')
    if (requested === 'maaltid') setTab('meals')
    if (requested === 'salg') setTab('market')
    if (requested === 'tip') setTab('tips')
    if (requested === 'moede') setTab('meetings')
    if (requested === 'mus') setTab('mus')
    if (requested === 'laerling') setTab('apprentices')
  }, [requested])
  const { data: unseen } = useIntranetUnseen()

  // Siden er aabnet - saa er det nye set, og tallet i menuen nulstilles
  // (beslutning 103). Opslag, der kraever kvittering, bliver staaende i tallet.
  useEffect(() => {
    markSeen.mutate()
    // Kun ved aabning: mutationen selv maa ikke udloese en ny koersel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Gruppen følger fanen - også når "+"-menuen eller ?fane= skifter fane.
  const activeTab = tabs.find((t) => t.key === tab) ?? tabs[0]
  const activeGroup = activeTab?.group ?? 'daily'
  const groups = GROUPS.filter((g) => tabs.some((t) => t.group === g.key))
  const groupCount = (group: TabGroup) =>
    tabs.filter((t) => t.group === group).reduce((sum, t) => sum + tabCount(t.key, unseen), 0)

  return (
    <div className="stack" style={{ gap: 16 }}>
      <nav className="intranet-menu" aria-label="Intranettets funktioner">
        <div className="intranet-menu__groups" role="tablist">
          {groups.map((group) => (
            <button
              key={group.key}
              type="button"
              role="tab"
              aria-selected={group.key === activeGroup}
              className={`intranet-chip intranet-chip--group${group.key === activeGroup ? ' is-active' : ''}`}
              // En gruppe åbner på sin første funktion.
              onClick={() => setTab(tabs.find((t) => t.group === group.key)!.key)}
            >
              <span aria-hidden>{group.icon}</span> {group.label}
              {groupCount(group.key) > 0 && <span className="intranet-chip__count">{groupCount(group.key)}</span>}
            </button>
          ))}
        </div>

        <div className="intranet-menu__tabs">
          {tabs
            .filter((item) => item.group === activeGroup)
            .map((item) => (
              <button
                key={item.key}
                type="button"
                className={`intranet-chip intranet-chip--tab${activeTab?.key === item.key ? ' is-active' : ''}`}
                onClick={() => setTab(item.key)}
              >
                <span aria-hidden>{item.icon}</span> {item.label}
                {tabCount(item.key, unseen) > 0 && (
                  <span className="intranet-chip__count">{tabCount(item.key, unseen)}</span>
                )}
              </button>
            ))}
        </div>
      </nav>

      {/* En fane, der er slået fra (beslutning 119), falder tilbage til Nyheder. */}
      {(tab === 'news' || !tabs.some((t) => t.key === tab)) && <NewsTab isAdmin={isAdmin} />}
      {tab === 'oncall' && <OnCallTab />}
      {tab === 'meetings' && <MeetingsTab />}
      {tab === 'mus' && <MusTab />}
      {tab === 'apprentices' && <ApprenticesTab />}
      {tab === 'meals' && <MealsTab />}
      {tab === 'kudos' && <KudosTab />}
      {tab === 'tips' && <TipsTab />}
      {tab === 'market' && <MarketTab />}
      {tab === 'events' && <EventsTab />}
      {tab === 'tasks' && <TasksTab />}
      {tab === 'incidents' && <WorkEnvironmentTab />}
      {tab === 'ideas' && <IdeasTab />}
      {tab === 'polls' && <PollsTab />}
      {tab === 'documents' && <DocumentsTab />}
      {tab === 'groups' && <GroupsTab />}
      {tab === 'phones' && <PhoneList />}
      {tab === 'links' && <LinksTab isAdmin={isAdmin} />}
    </div>
  )
}

/** Tallet paa den enkelte fane - samme opgoerelse som tallet i menuen. */
function tabCount(tab: Tab, unseen?: IntranetUnseen) {
  if (!unseen) return 0

  switch (tab) {
    case 'news':
      return unseen.posts + unseen.mustRead
    case 'events':
      return unseen.events
    case 'tasks':
      return unseen.tasks
    case 'incidents':
      return (unseen.incidents ?? 0) + (unseen.apv ?? 0)
    case 'ideas':
      return unseen.ideas ?? 0
    case 'polls':
      return unseen.polls ?? 0
    case 'documents':
      return unseen.documents
    case 'kudos':
      return unseen.kudos ?? 0
    case 'tips':
      return unseen.tips ?? 0
    case 'meals':
      return unseen.meals ?? 0
    case 'meetings':
      return unseen.meetings ?? 0
    case 'mus':
      return unseen.mus ?? 0
    default:
      return 0
  }
}

/** Opslagene. Kontoret skriver, alle læser. */
function NewsTab({ isAdmin }: { isAdmin: boolean }) {
  const { data, isLoading, error } = useIntranetPosts()
  const [editing, setEditing] = useState<IntranetPost | null | undefined>(undefined)
  useCreateRequest(['opslag'], () => {
    if (isAdmin) setEditing(null)
  })

  return (
    <div className="stack" style={{ gap: 16 }}>
      {/* Foedselsdage og jubilaeer (beslutning 117) - skjult, naar der ikke er nogen. */}
      <CelebrationsBox />
      {isAdmin && editing === undefined && (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn--primary" onClick={() => setEditing(null)}>
            Nyt opslag
          </button>
        </div>
      )}

      {editing !== undefined && <PostForm post={editing} onClose={() => setEditing(undefined)} />}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      {data?.length === 0 && (
        <EmptyState>
          Der er ikke skrevet noget endnu.
          {isAdmin ? ' Tryk "Nyt opslag" og fortæl huset noget.' : ''}
        </EmptyState>
      )}

      {data?.map((post) => (
        <PostCard key={post.id} post={post} onEdit={(p) => setEditing(p)} />
      ))}
    </div>
  )
}

/** Begivenhederne. Alle må oprette - også en fest. */
function EventsTab() {
  const [includePast, setIncludePast] = useState(false)
  const { data, isLoading, error } = useIntranetEvents(includePast)
  const [editing, setEditing] = useState<IntranetEvent | null | undefined>(undefined)
  useCreateRequest(['begivenhed'], () => setEditing(null))

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
        <label className="row small muted" style={{ gap: 6 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={includePast}
            onChange={(e) => setIncludePast(e.target.checked)}
          />
          Vis også overståede
        </label>

        {editing === undefined && (
          <button className="btn btn--primary" onClick={() => setEditing(null)}>
            Ny begivenhed
          </button>
        )}
      </div>

      {editing !== undefined && <EventForm event={editing} onClose={() => setEditing(undefined)} />}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}

      {data?.length === 0 && (
        <EmptyState>Ingen begivenheder. Tryk "Ny begivenhed" - foreslå gerne flere datoer.</EmptyState>
      )}

      {data?.map((event) => (
        <EventCard key={event.id} event={event} onEdit={(e) => setEditing(e)} />
      ))}
    </div>
  )
}

/** Genvejene til de andre systemer. Kontoret styrer dem. */
function LinksTab({ isAdmin }: { isAdmin: boolean }) {
  const { data } = useIntranetLinks(isAdmin)
  const save = useSaveIntranetLink()
  const remove = useDeleteIntranetLink()
  const [editing, setEditing] = useState<IntranetLink | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="stack" style={{ gap: 16 }}>
      {isAdmin && editing === undefined && (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn--primary" onClick={() => setEditing(null)}>
            Ny genvej
          </button>
        </div>
      )}

      {editing !== undefined && (
        <Card title={editing ? 'Ret genvejen' : 'Ny genvej'}>
          <form
            className="stack"
            onSubmit={async (event) => {
              event.preventDefault()
              setError(null)
              const form = new FormData(event.currentTarget)

              try {
                await save.mutateAsync({
                  id: editing?.id,
                  input: {
                    title: String(form.get('title') ?? '').trim(),
                    url: String(form.get('url') ?? '').trim(),
                    description: String(form.get('description') ?? '').trim() || null,
                    icon: String(form.get('icon') ?? '').trim() || null,
                    sortOrder: Number(form.get('sortOrder') ?? 0),
                    isActive: form.get('isActive') === 'on'
                  }
                })
                setEditing(undefined)
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Genvejen kunne ikke gemmes.')
              }
            }}
          >
            <div className="form-grid">
              <label className="field">
                <span className="field__label">Navn *</span>
                <input name="title" required maxLength={120} defaultValue={editing?.title} />
              </label>

              <label className="field">
                <span className="field__label">Adresse *</span>
                <input name="url" required maxLength={500} placeholder="https://..." defaultValue={editing?.url} />
              </label>

              <label className="field">
                <span className="field__label">Ikon</span>
                <input name="icon" maxLength={8} placeholder="F.eks. 📋" defaultValue={editing?.icon ?? ''} />
              </label>

              <label className="field">
                <span className="field__label">Rækkefølge</span>
                <input name="sortOrder" inputMode="numeric" defaultValue={editing?.sortOrder ?? 0} />
              </label>
            </div>

            <label className="field">
              <span className="field__label">Hvad er det?</span>
              <input name="description" maxLength={300} defaultValue={editing?.description ?? ''} />
            </label>

            <label className="row small" style={{ gap: 8 }}>
              <input
                type="checkbox"
                name="isActive"
                style={{ width: 'auto' }}
                defaultChecked={editing?.isActive ?? true}
              />
              Vises for alle
            </label>

            {error && <div className="error-box">{error}</div>}

            <div className="btn-group">
              <button className="btn btn--primary" type="submit" disabled={save.isPending}>
                Gem
              </button>
              <button className="btn" type="button" onClick={() => setEditing(undefined)}>
                Fortryd
              </button>
              {editing && (
                <button
                  className="btn"
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`Fjern genvejen "${editing.title}"?`)) return
                    await remove.mutateAsync(editing.id)
                    setEditing(undefined)
                  }}
                >
                  Fjern
                </button>
              )}
            </div>
          </form>
        </Card>
      )}

      {data?.length === 0 && <EmptyState>Ingen genveje endnu.</EmptyState>}

      <div className="grid grid--projects">
        {data?.map((link) => (
          <LinkTile key={link.id} link={link} onEdit={isAdmin ? () => setEditing(link) : undefined} />
        ))}
      </div>
    </div>
  )
}

/** Ét opslag - siden man lander på fra en besked. */
export function IntranetPostPage() {
  const { id } = useParams()
  const { data, isLoading, error } = useIntranetPost(Number(id))

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return <EmptyState>Opslaget findes ikke.</EmptyState>

  return <PostCard post={data} />
}

/** Én begivenhed - siden man lander på fra en besked. */
export function IntranetEventPage() {
  const { id } = useParams()
  const { data, isLoading, error } = useIntranetEvent(Number(id))

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return <EmptyState>Begivenheden findes ikke, eller du er ikke inviteret.</EmptyState>

  return <EventCard event={data} />
}
